import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, PenLine, Trash2 } from 'lucide-react';

import { signersApi } from '@/lib/api/signers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loading } from '@/components/Loading';
import { ConfirmButton } from '@/components/ConfirmButton';
import { EmptyState } from '@/components/ui/empty-state';
import { SignerUpsertDialog } from './SignerUpsertDialog';
import { useAuth } from '@/lib/auth';
import type { SignerDto } from '@paladin-monorepo/common';

export function SignersSettingsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSigner, setEditingSigner] = useState<SignerDto | null>(null);

  useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'SignersSettingsTab',
  });

  const { data: signers, isLoading } = useQuery({
    queryKey: ['signers'],
    queryFn: () => signersApi.list(),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => signersApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signers'] });
    },
  });

  const openCreate = () => {
    setEditingSigner(null);
    setDialogOpen(true);
  };

  const openEdit = (s: SignerDto) => {
    setEditingSigner(s);
    setDialogOpen(true);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingSigner(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>{t('signers.title')}</CardTitle>
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t('signers.list.add')}
          </Button>
        </CardHeader>
        <CardContent>
          {!signers || signers.length === 0 ? (
            <EmptyState
              icon={PenLine}
              title={t('signers.list.empty.title')}
              description={t('signers.list.empty.description')}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('signers.list.columns.name')}</TableHead>
                  <TableHead>{t('signers.list.columns.jobTitle')}</TableHead>
                  <TableHead>{t('signers.list.columns.email')}</TableHead>
                  <TableHead>{t('signers.list.columns.phone')}</TableHead>
                  <TableHead>{t('signers.list.columns.source')}</TableHead>
                  <TableHead>{t('signers.list.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {signers.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      {[s.firstName, s.lastName].filter(Boolean).join(' ') || '-'}
                    </TableCell>
                    <TableCell>{s.jobTitle || '-'}</TableCell>
                    <TableCell>{s.email}</TableCell>
                    <TableCell>{s.phone || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {s.linkedUserId
                          ? t('signers.list.source.user')
                          : t('signers.list.source.manual')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(s)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <ConfirmButton
                          title={t('signers.delete.title')}
                          description={t('signers.delete.description')}
                          onConfirm={async (): Promise<void> => {
                            await removeMutation.mutateAsync(s.id);
                          }}
                          variant="ghost"
                          disabled={removeMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </ConfirmButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SignerUpsertDialog
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        signer={editingSigner}
        onSignerCreatedIncomplete={(s) => setEditingSigner(s)}
      />
    </>
  );
}
